// THE WALL HOARD badges on the map — every Gem provider gets one automatically (day and night).
// The badge is the business's "read about us" card: logo, name, open status, highlights, their page link, and the Gems themselves.
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { Gem, ExternalLink } from "lucide-react";
import { GemSvg } from "./GemSvg";
import { DirectionsActions } from "../DirectionsActions";
import { openStatus } from "../../utils/openNow";
import { PlaceStar } from "./Highlights";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const abs = (u) => (u?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const cache = {};

const badgeIcon = (logo, night) => {
  const key = `${logo || ""}-${night ? 1 : 0}`;
  if (cache[key]) return cache[key];
  const size = 44;
  const inner = logo
    ? `<img src="${abs(logo)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block" loading="lazy"/>`
    : `<span style="display:flex;width:100%;height:100%;align-items:center;justify-content:center;font:900 16px 'Playfair Display',serif;color:#3A0A14;background:#F5D06F">G</span>`;
  cache[key] = L.divIcon({
    className: "",
    html: `<div data-testid="hoard-badge-pin" style="position:relative;width:${size}px;height:${size}px">
      <div style="width:${size}px;height:${size}px;border-radius:50%;overflow:hidden;background:#fff;border:3px solid #F5D06F;box-shadow:0 0 0 2px ${night ? "#120A24" : "#fff"},0 4px 12px rgba(0,0,0,.45)">${inner}</div>
      <span style="position:absolute;right:-6px;bottom:-4px;width:20px;height:20px;border-radius:50%;background:#3A0A14;border:2px solid #F5D06F;display:flex;align-items:center;justify-content:center;color:#F5D06F;font:900 10px/1 'Playfair Display',serif">◆</span>
    </div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size / 2], popupAnchor: [0, -size / 2 - 4],
  });
  return cache[key];
};

export const useHoardBusinesses = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API}/hoard/gems`, { withCredentials: true }).then((r) => setList((r.data.businesses || []).filter((b) => b.lat != null && b.lng != null))).catch(() => setList([]));
  }, []);
  return list;
};

export const HoardMarkers = ({ night = false }) => {
  const list = useHoardBusinesses();
  const refs = useRef({});
  const pending = useRef(null);
  const map = useMap();
  const navigate = useNavigate();
  // "wall:open-business-pin" (from the dock): fly to the business and open its badge once the marker exists.
  useEffect(() => {
    const go = () => { const id = pending.current; const m = id && refs.current[id]; if (m) { map.flyTo(m.getLatLng(), Math.max(map.getZoom(), 13), { duration: 0.8 }); setTimeout(() => m.openPopup(), 850); pending.current = null; window.__wallPendingPin = null; } };
    const h = (e) => { pending.current = e.detail?.business_id; go(); };
    window.addEventListener("wall:open-business-pin", h);
    if (window.__wallPendingPin) { pending.current = window.__wallPendingPin; }
    go();
    return () => window.removeEventListener("wall:open-business-pin", h);
  }, [list, map]);
  return list.map((b) => {
    const st = openStatus(b.hours);
    const site = b.website ? (b.website.startsWith("http") ? b.website : `https://${b.website}`) : "";
    return (
      <Marker key={b.business_id} position={[b.lat, b.lng]} icon={badgeIcon(b.logo_url, night)} zIndexOffset={900} ref={(m) => { if (m) refs.current[b.business_id] = m; }}>
        <Popup maxWidth={300} minWidth={230}>
          <div className="min-w-[220px]" data-testid={`hoard-badge-${b.business_id}`}>
            <div className="flex items-start gap-2">
              {b.logo_url ? <img src={abs(b.logo_url)} alt="" className="h-11 w-11 rounded-full object-cover border-2 border-[#F5D06F] shrink-0" /> : <span className="h-11 w-11 rounded-full bg-[#F5D06F] inline-flex items-center justify-center shrink-0"><Gem className="h-5 w-5 text-[#3A0A14]" /></span>}
              <div className="min-w-0 flex-1">
                <div className="font-bold text-[14px] text-[#1A1A1A] leading-tight flex items-center gap-1">{b.name} <PlaceStar business={b} size={14} /></div>
                <div className="text-[11px] font-semibold mt-0.5 text-[#8A6508]">◆ THE WALL HOARD · {b.gems.length} Gem{b.gems.length === 1 ? "" : "s"}{b.mine ? " · yours" : ""}</div>
              </div>
            </div>
            {!!b.highlights?.length && <p className="m-0 mt-1 text-[12px] text-[#5C5C5C] italic" data-testid={`hoard-badge-highlights-${b.business_id}`}>Try: {b.highlights.join(" · ")}</p>}
            {b.address && <p className="m-0 mt-1 text-[11px] text-[#5C5C5C]">{b.address}{b.city ? `, ${b.city}` : ""}</p>}
            {st && <p className={`m-0 mt-1 text-[11px] font-bold ${st.status === "open" ? "text-[#15803d]" : "text-[#6b7280]"}`}>{st.status === "open" ? "● " : "○ "}{st.label}</p>}
            <ul className="m-0 mt-2 p-0 list-none space-y-1" data-testid={`hoard-badge-gems-${b.business_id}`}>
              {b.gems.map((g) => (
                <li key={g.item_id}>
                  <button type="button" onClick={() => navigate(b.redeem_page)} className="w-full text-left flex items-center gap-2 rounded-lg px-2 py-1 bg-[#FBF7EE] hover:bg-[#F5D06F]/30 border border-[#E8E2D4]" title="Redeem on their page" data-testid={`hoard-badge-gem-${g.item_id}`}>
                    {g.photo_url ? <img src={abs(g.photo_url)} alt="" className="h-7 w-7 rounded-md object-cover shrink-0" /> : <GemSvg cut={g.cut} color={g.color} size={18} title={g.stone} />}
                    <span className="min-w-0 flex-1 text-[12px] font-semibold text-[#1A1A1A] leading-tight truncate">{g.title}{g.preview ? <span className="ml-1 text-[9px] font-black text-[#B45309]">PENDING</span> : null}</span>
                    <span className="text-[10px] font-black text-[#8A6508]">Redeem →</span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Link to={b.page} className="text-[11px] font-bold text-[#2E5C55] underline" data-testid={`hoard-badge-page-${b.business_id}`}>Read about them →</Link>
              {site && <a href={site} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1d4ed8]">Website <ExternalLink className="h-3 w-3" /></a>}
            </div>
            <div className="border-t border-[#E2E0DC] mt-2 pt-2">
              <DirectionsActions variant="compact" size="xs" lat={b.lat} lng={b.lng} address={`${b.address || ""} ${b.city || ""}`.trim()} testId={`hoard-badge-directions-${b.business_id}`} />
            </div>
          </div>
        </Popup>
      </Marker>
    );
  });
};
